import React, { useRef, useEffect, useState } from 'react';
import { useCarousel } from '../../../hooks/useCarousel';
import { classNames } from '../../../utils/helpers';
import styles from './Carousel.module.css';

const Carousel = ({ 
  images = [], 
  autoAdvance = true, 
  showDots = true,
  className = '' 
}) => {
  const {
    currentIndex,
    goTo,
    totalItems,
  } = useCarousel(images, autoAdvance);

  const dotRefs = useRef([]);
  const imageRef = useRef(null);
  const [displayedImage, setDisplayedImage] = useState(images[currentIndex]);
  const [containerHeight, setContainerHeight] = useState('auto');
  const [isSliding, setIsSliding] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [visibleImages, setVisibleImages] = useState({});

  // Handle image loading states - only show when fully loaded
  const handleImageLoad = (src) => {
    console.log(`✅ Image fully loaded: ${src}`);
    setImageLoadStates(prev => ({ ...prev, [src]: true }));
    
    // Small delay to ensure image is fully rendered
    setTimeout(() => {
      setVisibleImages(prev => ({ ...prev, [src]: true }));
    }, 50);
  };

  const handleImageError = (src) => {
    console.warn(`❌ Image failed to load: ${src}`);
    setImageLoadStates(prev => ({ ...prev, [src]: false }));
  };

  // Preload all images and track their loading states
  useEffect(() => {
    images.forEach((src) => {
      if (!imageLoadStates[src]) {
        const img = new Image();
        img.onload = () => handleImageLoad(src);
        img.onerror = () => handleImageError(src);
        img.src = src;
      }
    });
  }, [images, imageLoadStates]);
  // Calculate container height based on the current image
  useEffect(() => {
    if (imageRef.current && visibleImages[displayedImage]) {
      const updateHeight = () => {
        const height = imageRef.current.clientHeight;
        setContainerHeight(`${height}px`);
      };
      
      // Set initial height
      updateHeight();
      
      // Update height on window resize
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
  }, [displayedImage, visibleImages]);

  // Handle smooth slide transition
  useEffect(() => {
    if (images[currentIndex] !== displayedImage) {
      setIsSliding(true);
      
      // Wait just a tiny bit longer than the animation to ensure smooth handoff
      const timer = setTimeout(() => {
        // Use requestAnimationFrame to ensure smooth transition on next frame
        requestAnimationFrame(() => {
          setDisplayedImage(images[currentIndex]);
          setIsSliding(false);
        });
      }, 820); // Slightly longer than 800ms animation
      
      return () => clearTimeout(timer);
    }
  }, [currentIndex, images, displayedImage]);

  // Remove focus from all dots when currentIndex changes (auto-advance)
  useEffect(() => {
    dotRefs.current.forEach(dot => {
      if (dot && document.activeElement === dot) {
        dot.blur();
      }
    });
  }, [currentIndex]);

  if (!images.length) return null;

  const isDisplayedImageVisible = visibleImages[displayedImage];

  return (
    <div className={classNames(styles.carousel, className)}>
      <div className={styles.imageWrapper}>
        <div className={styles.imageContainer} style={{ height: containerHeight }}>
          {/* Loading placeholder - show while image is loading */}
          {!isDisplayedImageVisible && (
            <div className={styles.imagePlaceholder}>
              <div className={styles.loadingSpinner}></div>
            </div>
          )}

          {/* Current image - normal or sliding out - only show when fully loaded */}
          {isDisplayedImageVisible && (
            <img 
              ref={!isSliding ? imageRef : null}
              src={displayedImage} 
              alt={`Screenshot ${currentIndex + 1}`}
              className={classNames(
                styles.image,
                isSliding ? styles.imageSlideOut : ''
              )}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              onLoad={() => handleImageLoad(displayedImage)}
              onError={() => handleImageError(displayedImage)}
            />
          )}
          
          {/* Next image - sliding in from right - only show when fully loaded */}
          {isSliding && images[currentIndex] !== displayedImage && visibleImages[images[currentIndex]] && (
            <img 
              ref={imageRef}
              src={images[currentIndex]} 
              alt={`Screenshot ${currentIndex + 1}`}
              className={classNames(styles.image, styles.imageSlideIn)}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              onLoad={() => handleImageLoad(images[currentIndex])}
              onError={() => handleImageError(images[currentIndex])}
            />
          )}
        </div>
      </div>
      
      {showDots && totalItems > 1 && (
        <div className={styles.dots}>
          {images.map((_, index) => {
            const isActive = index === currentIndex;
            return (
              <button
                key={index}
                ref={el => dotRefs.current[index] = el}
                onClick={() => goTo(index)}
                className={classNames(
                  styles.dot,
                  isActive ? styles.dotActive : ''
                )}
                aria-label={`Go to image ${index + 1}`}
                aria-pressed={isActive}
                tabIndex={0}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Carousel;